import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../contractorApi';
import { storageBaseUrl } from '../../api';

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

const fmt = (n) => Number(n || 0).toLocaleString();

const docNo = (prefix, id, date) => {
    const d = date ? new Date(date) : new Date();
    return `${prefix}-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(id).padStart(3, '0')}`;
};

const fmtDate = (val) => {
    if (!val) return '';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return val; }
};

// ── アイコン ──────────────────────────────────────────────────
const ArrowLeftIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
);
const PrinterIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
    </svg>
);
const DownloadIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

// ── A4ドキュメント ────────────────────────────────────────────
const A4Document = ({ projectId, data }) => {
    const { invoiceDate, dueDate, customerName, customerPostal, customerAddress, contractor, breakdown, note, subtotal, tax, totalAmount } = data;
    const rowAmount = (row) => Math.round((Number(row.quantity) || 0) * (Number(row.unit_price) || 0));
    const emptyRowCount = Math.max(0, 5 - breakdown.length);

    return (
        <div style={{ padding: '32px', fontSize: '11px', lineHeight: 1.6, color: '#1a1a2e' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>請求書</h1>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, gap: 16 }}>
                <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 700, margin: '0 0 2px', fontSize: '13px' }}>{customerName} 様</p>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '10px' }}>〒{customerPostal}</p>
                    <p style={{ color: '#6b7280', margin: '0 0 12px', fontSize: '10px' }}>{customerAddress}</p>
                    <p style={{ margin: 0, fontSize: '10px' }}>件名: {customerName} 様 乗り入れ工事</p>
                </div>
                <div style={{ textAlign: 'right', fontSize: '10px' }}>
                    <div><span style={{ color: '#6b7280' }}>発行日</span><span style={{ marginLeft: 12 }}>{fmtDate(invoiceDate)}</span></div>
                    <div><span style={{ color: '#6b7280' }}>請求番号</span><span style={{ marginLeft: 12 }}>{docNo('S', projectId, invoiceDate)}</span></div>
                    {dueDate && <div><span style={{ color: '#6b7280' }}>支払期限</span><span style={{ marginLeft: 12 }}>{fmtDate(dueDate)}</span></div>}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: 700, margin: '0 0 2px', fontSize: '10px' }}>{contractor?.company_name}</p>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '9px' }}>{contractor?.representative}</p>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '9px' }}>〒{contractor?.postal_code}</p>
                    <p style={{ color: '#6b7280', margin: '0 0 8px', fontSize: '9px' }}>{contractor?.prefecture}{contractor?.city}{contractor?.address}</p>
                    {contractor?.seal_image ? (
                        <img src={`${storageBaseUrl}/${contractor.seal_image}`} alt="印鑑" style={{ width: 40, height: 40, objectFit: 'contain', opacity: 0.82, mixBlendMode: 'multiply' }} />
                    ) : (
                        <div style={{ display: 'inline-flex', width: 40, height: 40, border: '2px solid #ef4444', borderRadius: '50%', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: '9px' }}>印</div>
                    )}
                </div>
            </div>

            <div style={{ border: '2px solid #1a1a2e', borderRadius: 4, padding: '12px', marginBottom: 24, background: '#f9fafb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px' }}>
                        <div style={{ display: 'flex', gap: 24, marginBottom: 2 }}>
                            <span style={{ color: '#6b7280' }}>小計</span><span style={{ fontWeight: 500 }}>{fmt(subtotal)}円</span>
                        </div>
                        <div style={{ display: 'flex', gap: 24 }}>
                            <span style={{ color: '#6b7280' }}>消費税</span><span style={{ fontWeight: 500 }}>{fmt(tax)}円</span>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '20px', fontWeight: 700 }}>{fmt(totalAmount)}円</div>
                        <div style={{ fontSize: '9px', color: '#6b7280' }}>請求金額</div>
                    </div>
                </div>
            </div>

            <div style={{ border: '1px solid #1a1a2e', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #1a1a2e' }}>
                            {[['摘要', 'left', ''], ['数量', 'center', '64px'], ['単位', 'center', '48px'], ['単価（円）', 'right', '80px'], ['金額', 'right', '96px']].map(([h, align, w]) => (
                                <th key={h} style={{ padding: '8px', textAlign: align, background: '#f9fafb', fontWeight: 600, borderRight: h !== '金額' ? '1px solid #1a1a2e' : 'none', width: w || 'auto' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {breakdown.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #e0e4ea' }}>
                                <td style={{ padding: '8px', borderRight: '1px solid #e0e4ea' }}>{row.name || '—'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #e0e4ea' }}>{row.quantity}</td>
                                <td style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #e0e4ea' }}>{row.unit}</td>
                                <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e0e4ea' }}>{fmt(row.unit_price)}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(rowAmount(row))}</td>
                            </tr>
                        ))}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`e${i}`} style={{ borderBottom: '1px solid #e0e4ea' }}>
                                {[...Array(5)].map((_, j) => <td key={j} style={{ padding: '8px', borderRight: j < 4 ? '1px solid #e0e4ea' : 'none' }}>&nbsp;</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <div style={{ border: '1px solid #e0e4ea', width: 280, fontSize: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px' }}>
                        <span style={{ color: '#6b7280' }}>消費税</span>
                        <span>10%対象{fmt(subtotal)}</span>
                        <span>{fmt(tax)}円</span>
                    </div>
                </div>
            </div>

            {note && (
                <div style={{ paddingTop: 12, borderTop: '1px solid #e0e4ea' }}>
                    <p style={{ fontWeight: 700, margin: '0 0 4px', fontSize: '10px' }}>備考</p>
                    <pre style={{ color: '#6b7280', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '9px', margin: 0 }}>{note}</pre>
                </div>
            )}
        </div>
    );
};

// ── メイン ────────────────────────────────────────────────────
const InvoicePreviewPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const data = location.state;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const update = () => setScale(el.offsetWidth / A4_WIDTH);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    if (!data) {
        navigate(`/contractor/invoice/create/${projectId}`);
        return null;
    }

    const handleSend = async () => {
        setLoading(true);
        setError(null);
        try {
            const filledRows = data.breakdown.filter(r => r.name);
            const rowAmount = (row) => Math.round((Number(row.quantity) || 0) * (Number(row.unit_price) || 0));
            await api.post('invoices', {
                project_id: Number(projectId),
                amount: data.totalAmount,
                breakdown: filledRows.map(r => ({
                    name: r.name,
                    quantity: Number(r.quantity) || 1,
                    unit: r.unit,
                    unit_price: Number(r.unit_price) || 0,
                    amount: rowAmount(r),
                })),
                note: data.note,
                due_date: data.dueDate || null,
            });
            navigate(`/contractor/project/${projectId}`);
        } catch {
            setError('送信に失敗しました。');
            setLoading(false);
        }
    };

    const GHOST_BTN = {
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 16px', border: '1px solid #e0e4ea', borderRadius: 4,
        background: '#fff', color: '#1a1a2e', cursor: 'pointer', fontSize: '0.875rem',
        transition: 'background 0.15s',
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8' }}>
            <style>{`
                .prev-desktop-header { display: flex; }
                .prev-mobile-header { display: none; }
                .prev-mobile-bar { display: none; }
                @media (max-width: 768px) {
                    .prev-desktop-header { display: none !important; }
                    .prev-mobile-header { display: flex !important; }
                    .prev-mobile-bar { display: flex !important; }
                    .prev-body { padding-bottom: 88px !important; }
                }
                @media print {
                    @page { size: A4; margin: 15mm; }
                    body { background: #fff !important; }
                    .no-print { display: none !important; }
                    .prev-body { padding: 0 !important; max-width: 100% !important; margin: 0 !important; }
                    .print-area { border: none !important; box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; height: auto !important; }
                    .a4-content { transform: none !important; width: 100% !important; height: auto !important; }
                }
            `}</style>

            {/* デスクトップヘッダー */}
            <div className="prev-desktop-header no-print" style={{ background: '#fff', borderBottom: '1px solid #e0e4ea', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px' }}>
                <button onClick={() => navigate(-1)} style={{ ...GHOST_BTN, border: 'none', padding: '8px 0' }}>
                    <ArrowLeftIcon /><span>戻る</span>
                </button>
                <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>請求書の確認</h1>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <button onClick={() => window.print()} style={GHOST_BTN}><PrinterIcon /><span>印刷 / PDF保存</span></button>
                    {error && <span style={{ color: '#ef4444', fontSize: '0.82rem' }}>{error}</span>}
                    <button onClick={handleSend} disabled={loading} style={{ padding: '8px 20px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.9rem', fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}>
                        {loading ? '送信中...' : 'この内容で送信する'}
                    </button>
                </div>
            </div>

            {/* モバイルヘッダー */}
            <div className="prev-mobile-header no-print" style={{ background: '#fff', borderBottom: '1px solid #e0e4ea', alignItems: 'center', padding: '12px 16px' }}>
                <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#1a1a2e', cursor: 'pointer', padding: 0, display: 'flex' }}>
                    <ArrowLeftIcon />
                </button>
                <h1 style={{ flex: 1, textAlign: 'center', fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>請求書の確認</h1>
                <div style={{ width: 20 }} />
            </div>

            {/* ボディ */}
            <div className="prev-body" style={{ maxWidth: 840, margin: '0 auto', padding: '24px 16px' }}>
                <div ref={containerRef} className="print-area" style={{ borderRadius: 8, border: '1px solid #e0e4ea', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', height: A4_HEIGHT * scale }}>
                    <div className="a4-content" style={{ width: A4_WIDTH, height: A4_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'top left', background: '#fff' }}>
                        <A4Document projectId={projectId} data={data} />
                    </div>
                </div>
            </div>

            {/* モバイル固定下部バー */}
            <div className="prev-mobile-bar no-print" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e0e4ea', padding: '12px 16px', gap: 10, alignItems: 'center' }}>
                <button onClick={() => window.print()} style={{ ...GHOST_BTN, padding: '12px' }}><PrinterIcon /></button>
                {error && <span style={{ color: '#ef4444', fontSize: '0.78rem', flex: 1 }}>{error}</span>}
                <button onClick={handleSend} disabled={loading} style={{ flex: 1, padding: '12px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.9rem', fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}>
                    {loading ? '送信中...' : '送信する'}
                </button>
            </div>
        </div>
    );
};

export default InvoicePreviewPage;
